import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, ChevronRight, Camera } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { t } from "../lib/i18n";

async function fileToResizedDataURL(file, maxSize = 1200, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function PhotoUpload() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const photos = user?.photos || [];
  const lang = user?.language || "ru";

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (photos.length >= 5) return toast.error("Максимум 5 фото");
    setBusy(true);
    const toastId = toast.loading("Загружаем и проверяем…");
    try {
      const dataUrl = await fileToResizedDataURL(file);
      const { data } = await api.post("/profile/photo", { data_url: dataUrl });
      await refresh();
      toast.success("Фото добавлено!", { id: toastId });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ошибка загрузки", { id: toastId });
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/profile/photo/${id}`);
      await refresh();
    } catch {
      toast.error("Не удалось удалить");
    }
  };

  const finish = async () => {
    if (photos.length === 0) return toast.error(t(lang, "photo_need_one"));
    await refresh();
    setTimeout(() => nav("/complete-profile", { replace: true }), 100);
  };

  return (
    <div className="kupidon-shell safe-top safe-bottom flex flex-col relative">
      <div className="px-6 pt-6">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--ink-soft)]">Шаг 2</div>
        <h1 className="font-[Onest] font-extrabold text-4xl tracking-tight mt-1">Твои фото</h1>
        <p className="mt-2 text-[color:var(--ink-soft)] text-sm">
          Загрузи 1–5 фото. Чем больше у тебя — тем больше чужих ты видишь <em>без размытия</em>.
        </p>
      </div>

      <div className="px-6 mt-6 grid grid-cols-2 gap-3" data-testid="photo-grid">
        {photos.map((p, i) => (
          <motion.div
            key={p.id}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative aspect-[3/4] rounded-3xl overflow-hidden shadow-md shadow-[#E76F51]/15"
            data-testid={`photo-slot-${i}`}
          >
            <img src={p.data_url} alt="" className="w-full h-full object-cover" />
            <button
              data-testid={`photo-remove-${p.id}`}
              onClick={() => remove(p.id)}
              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white"
            >
              <X className="w-4 h-4" />
            </button>
            {i === 0 && (
              <div className="absolute bottom-2 left-2 px-2 py-1 rounded-full bg-[color:var(--peach)] text-white text-[10px] font-semibold">
                Главное
              </div>
            )}
          </motion.div>
        ))}
        {photos.length < 5 && (
          <label
            data-testid="photo-add"
            className="aspect-[3/4] rounded-3xl border-2 border-dashed border-[color:var(--peach)]/50 bg-white/50 flex flex-col items-center justify-center gap-2 cursor-pointer hover:bg-white/80 transition-colors"
          >
            <div className="w-12 h-12 rounded-2xl bg-[color:var(--peach)]/15 flex items-center justify-center">
              {busy ? (
                <div className="w-5 h-5 border-2 border-[color:var(--peach)] border-t-transparent rounded-full animate-spin" />
              ) : (
                <Camera className="w-6 h-6 text-[color:var(--peach-deep)]" />
              )}
            </div>
            <div className="text-xs font-medium text-[color:var(--ink-soft)]">
              {busy ? "Загружаем…" : "Добавить"}
            </div>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFile}
              disabled={busy}
            />
          </label>
        )}
      </div>

      <div className="flex-1" />

      <div className="px-6 pb-6 pt-4">
        <button
          data-testid="photos-finish"
          onClick={finish}
          disabled={photos.length === 0}
          className="btn-pill w-full bg-[color:var(--peach)] text-white flex items-center justify-center gap-2 disabled:opacity-50"
        >
          В приложение
          <ChevronRight className="w-5 h-5" />
        </button>
        <p className="text-xs text-center mt-3 text-[color:var(--ink-soft)]">
          {photos.length} из 5 фото загружено
        </p>
      </div>
    </div>
  );
}
